import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Pagination, FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Menu, ListItemIcon, ListItemText, Button
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Article from './Article';
import EditArticle from './EditArticle';
import DashboardLayout from '../components/DashboardLayout';
import ReporterRoutes from './ReporterRoutes';

// Dashboard Home Component
function DashboardHome() {
  const [stats, setStats] = useState({
    totalArticles: 0,
    published: 0,
    pendingReview: 0,
    totalViews: 0
  });
  const [loading, setLoading] = useState(true);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    return { sessionId, dashAdminId };
  };

  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      const { sessionId, dashAdminId } = getSessionData();
      
      if (!sessionId || !dashAdminId) {
        console.error('❌ Session data not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('https://api.jpnewz.com/dashboard/list_all_posts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId,
            dashAdminId,
            skip: 0,
            limit: 1000,
            isSearchFilter: false,
            search: ""
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.data && Array.isArray(result.data)) {
            const total = result.data.length;
            const published = result.data.filter(post => post.postStatus === 4).length;
            const pending = result.data.filter(post => post.postStatus === 1).length;
            
            setStats({
              totalArticles: total,
              published: published,
              pendingReview: pending,
              totalViews: 0 // Can be calculated if view data is available
            });
          }
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
        Reporter Dashboard Overview
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4" color="primary" gutterBottom>
              {loading ? '...' : stats.totalArticles}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              My Articles
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#f3e5f5' }}>
            <Typography variant="h4" color="secondary" gutterBottom>
              {loading ? '...' : stats.published}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Published
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e8' }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              {loading ? '...' : stats.pendingReview}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Pending Review
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography variant="h4" color="warning.main" gutterBottom>
              {loading ? '...' : stats.totalViews}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Total Views
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// My Articles Component (NewsView for Reporter)
function MyArticles({ onViewArticle }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPostStatus, setSelectedPostStatus] = useState(''); // Add post status filter state
  const [posts, setPosts] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Reporter Articles:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch posts data
  const fetchPosts = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for reporter posts');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const requestBody = {
        sessionId,
        dashAdminId,
        skip: (currentPage - 1) * pageSize, // Convert to 0-based index
        limit: pageSize,
        isSearchFilter: searchTerm.trim() !== "",
        search: searchTerm.trim(),
        ispostStatusFilter: selectedPostStatus !== "",
        postStatus: selectedPostStatus
      };

      console.log('📤 Fetching reporter posts with:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Reporter posts fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort posts by createdAt in descending order (latest created first)
          const sortedPosts = result.data.sort((a, b) => {
            // Use createdAt as primary sorting parameter
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB - dateA; // Descending order (latest created first)
          });
          setPosts(sortedPosts);
          
          // Set total count and calculate total pages
          const total = result.totalCount || result.data.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setPosts([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching reporter posts:', errorData);
        setPosts([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching reporter posts:', error);
      setPosts([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, searchTerm, selectedPostStatus]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Get post type label
  const getPostTypeLabel = (postType) => {
    switch (postType) {
      case 1: return 'News';
      case 2: return 'Article';
      case 3: return 'Video';
      default: return 'Unknown';
    }
  };

  // Get news type label
  const getNewsTypeLabel = (newsType) => {
    switch (newsType) {
      case 1: return 'Breaking';
      case 2: return 'Regular';
      default: return 'Unknown';
    }
  };

  // Get post status label
  const getPostStatusLabel = (postStatus) => {
    switch (postStatus) {
      case 1: return 'Verification';
      case 2: return 'Approved';
      case 3: return 'Rejected';
      case 4: return 'Published';
      case 5: return 'Unpublished';
      default: return 'Unknown';
    }
  };

  // Get post status color
  const getPostStatusColor = (postStatus) => {
    switch (postStatus) {
      case 1: return 'warning.main'; // Verification - orange
      case 2: return 'success.main'; // Approved - green
      case 3: return 'error.main';   // Rejected - red
      case 4: return 'primary.main'; // Published - blue
      case 5: return 'secondary.main'; // Unpublished - gray
      default: return 'text.secondary';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return { date: dateStr, time: timeStr };
  };

  const handleViewArticle = (post) => {
    console.log('View article:', post);
    // Navigate to EditArticle in read-only mode
    if (onViewArticle) {
      onViewArticle(post.postId, post);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, post) => {
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  const handleEdit = () => {
    if (selectedPost && onViewArticle) {
      onViewArticle(selectedPost.postId, selectedPost);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedPost && onViewArticle) {
      onViewArticle(selectedPost.postId, selectedPost);
    }
    handleMenuClose();
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Reset to first page when search term or post status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPostStatus]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600, mb: 2 }}>
          My Articles
        </Typography>
        
        {/* Post Status Filter Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant={selectedPostStatus === '' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            All
          </Button>
          <Button
            variant={selectedPostStatus === '1' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('1')}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              ...(selectedPostStatus === '1' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Verification
          </Button>
          <Button
            variant={selectedPostStatus === '2' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('2')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '2' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Approved
          </Button>
          <Button
            variant={selectedPostStatus === '3' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('3')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '3' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Rejected
          </Button>
          <Button
            variant={selectedPostStatus === '4' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('4')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '4' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Published
          </Button>
          <Button
            variant={selectedPostStatus === '5' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('5')}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              ...(selectedPostStatus === '5' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Unpublished
          </Button>
        </Box>
      </Box>
      
      {/* Search Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
            <TextField
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
          </Box>
          {/* Commented out Per Page selection logic */}
          {/* <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label="Per Page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl> */}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            label={`Total: ${totalCount}`} 
            color="primary" 
            variant="outlined" 
            size="small" 
          />
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
          {/* Pagination Controls - Beside total count */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.75rem',
                  minWidth: '28px',
                  height: '28px',
                },
              }}
            />
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Created Date</TableCell>
              <TableCell>Media</TableCell>
              <TableCell>Title & Description</TableCell>
              <TableCell>Reporter Name</TableCell>
              <TableCell>Engagement</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading articles...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No articles found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post, index) => (
                <TableRow 
                  key={post.postId}
                  onClick={() => {
                    console.log('🖱️ Article row clicked:', post);
                    // Navigate to view-only article page
                    handleViewArticle(post);
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <TableCell sx={{ width: '120px' }}>
                    {formatDate(post.createdAt) !== 'N/A' ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {formatDate(post.createdAt).date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatDate(post.createdAt).time}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {post.mediaArray && post.mediaArray.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {post.mediaArray.slice(0, 2).map((media, mediaIndex) => (
                          <Box
                            key={mediaIndex}
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 1,
                              border: '1px solid #ddd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: media.mediaType === 2 ? '#e3f2fd' : '#f5f5f5'
                            }}
                          >
                            {media.mediaType === 1 && media.imageUrl ? (
                              <Box
                                component="img"
                                src={media.imageUrl}
                                alt={`Image ${mediaIndex + 1}`}
                                sx={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover',
                                  borderRadius: 1
                                }}
                              />
                            ) : media.mediaType === 2 ? (
                              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                🎥
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                ?
                              </Typography>
                            )}
                          </Box>
                        ))}
                        {post.mediaArray.length > 2 && (
                          <Box
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 1,
                              bgcolor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #ddd'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              +{post.mediaArray.length - 2}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No media
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.95rem' }}>
                        {post.title}
                      </Typography>
                      {post.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {post.description}
                        </Typography>
                      )}
                      {post.hashTags && (
                        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                          {post.hashTags}
                        </Typography>
                      )}
                      {/* News Type and Post Status under title/description */}
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {getNewsTypeLabel(post.newsType)}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: getPostStatusColor(post.postStatus) === 'warning.main' ? '#fff3e0' :
                                     getPostStatusColor(post.postStatus) === 'success.main' ? '#e8f5e8' :
                                     getPostStatusColor(post.postStatus) === 'error.main' ? '#ffebee' :
                                     getPostStatusColor(post.postStatus) === 'secondary.main' ? '#f5f5f5' :
                                     '#e3f2fd',
                            border: `1px solid ${
                              getPostStatusColor(post.postStatus) === 'warning.main' ? '#ff9800' :
                              getPostStatusColor(post.postStatus) === 'success.main' ? '#4caf50' :
                              getPostStatusColor(post.postStatus) === 'error.main' ? '#f44336' :
                              getPostStatusColor(post.postStatus) === 'secondary.main' ? '#9e9e9e' :
                              '#2196f3'
                            }`,
                            display: 'inline-block'
                          }}
                        >
                          <Typography 
                            variant="caption"
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              color: getPostStatusColor(post.postStatus) === 'warning.main' ? '#e65100' :
                                     getPostStatusColor(post.postStatus) === 'success.main' ? '#2e7d32' :
                                     getPostStatusColor(post.postStatus) === 'error.main' ? '#d32f2f' :
                                     getPostStatusColor(post.postStatus) === 'secondary.main' ? '#616161' :
                                     '#1565c0'
                            }}
                          >
                            {getPostStatusLabel(post.postStatus)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {post.createdAdminName || 'Unknown Reporter'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        👍 {post.noOfLikes || 0} | 👎 {post.noOfDisLikes || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        📖 {post.noOfBookmarks || 0} | 👁️ {post.noOfViews || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, post);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      </Menu>

    </Box>
  );
}

// Export individual components for routing
export { DashboardHome, MyArticles };

export default function ReporterDashboard({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get current route and map it to selected tab
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/myarticles')) return 'News';
    if (path.includes('/addnewsarticle')) return 'Add News Article';
    return 'Dashboard';
  };
  
  const [selected, setSelected] = useState(getCurrentTab);
  const [viewingArticle, setViewingArticle] = useState(null);
  
  // Edit article state
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [editingArticleData, setEditingArticleData] = useState(null);

  // Update selected tab when route changes
  useEffect(() => {
    setSelected(getCurrentTab());
  }, [location.pathname]);
  
  // Save active tab to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('reporterActiveTab', selected);
  }, [selected]);

  // Navigation items for Reporter
  const navigationItems = [
    { key: 'Dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { key: 'News', label: 'News', icon: <ArticleIcon /> },
    { key: 'Add News Article', label: 'Add News Article', icon: <AddCircleOutlineIcon /> },
  ];

  const handleViewArticle = (articleId, articleData) => {
    console.log('🖱️ Navigating to view article:', articleId, articleData);
    setViewingArticle({ articleId, articleData });
    setEditingArticleId(articleId);
    setEditingArticleData(articleData);
  };

  const handleBackFromArticle = () => {
    setViewingArticle(null);
    setEditingArticleId(null);
    setEditingArticleData(null);
  };

  // Navigation handler
  const handleNavigationChange = (tabKey) => {
    setSelected(tabKey);
    // Navigate to the corresponding route
    switch (tabKey) {
      case 'Dashboard':
        navigate('/reporter/dashboard');
        break;
      case 'News':
        navigate('/reporter/myarticles');
        break;
      case 'Add News Article':
        navigate('/reporter/addnewsarticle');
        break;
      default:
        navigate('/reporter/dashboard');
    }
  };

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      selected={selected}
      onNavigationChange={handleNavigationChange}
      navigationItems={navigationItems}
      userRole="Reporter"
      accessLevel="Basic Access"
    >
      <ReporterRoutes
        onViewArticle={handleViewArticle}
        editingArticleId={editingArticleId}
        setEditingArticleId={setEditingArticleId}
        editingArticleData={editingArticleData}
        setEditingArticleData={setEditingArticleData}
      />
    </DashboardLayout>
  );
} 